import { G } from '@mobily/ts-belt';
import { CreateInDialogButtonProps, useFormDialogContext } from '@react-admin/ra-form-layout';
import { useCallback, useState } from 'react';
import {
  Confirm,
  RaRecord,
  useDataProvider,
  useGetIdentity,
  useNotify,
  useRedirect,
  useRefresh,
} from 'react-admin';
import { FieldValues, UseFormReturn } from 'react-hook-form';

import { GetB2bEmployeesEndpoint } from '@Common/types/bo/b2b/employees';

import { CreateInDialogButton, StepperForm, StepperFormStep } from '@sh4res/bo-common/components';
import { ContactStep } from '@sh4res/bo-common/components/B2bEmployee/steps/ContactStep';
import { EmploymentInformationStep } from '@sh4res/bo-common/components/B2bEmployee/steps/EmployeeInformationStep';
import { PersonalInformationStep } from '@sh4res/bo-common/components/B2bEmployee/steps/PersonalInformationStep';
import { createEmployeeEmailsValidator } from '@sh4res/bo-common/components/B2bEmployee/validateEmployeeEmails';
import { useCanAccess, useCreatePromise } from '@sh4res/bo-common/hooks';
import { getErrorMessage, Resources } from '@sh4res/bo-common/utils';
import { isBoExternalIdentity } from '@sh4res/bo-common/utils/b2b_auth_role';
import { buildLink } from '@sh4res/bo-common/utils/build_link';

type B2bEmployeeCreateProps = CreateInDialogButtonProps['ButtonProps'] & {
  allowCompanyId?: boolean;
  resource: Resources;
};

export const B2bEmployeeCreate = ({
  allowCompanyId,
  resource,
  ...buttonProps
}: B2bEmployeeCreateProps) => {
  const testId = 'BoExternalB2bEmployeeCreate';
  const canAccess = useCanAccess({
    resource,
    action: 'create',
  });

  if (resource !== 'employees' && resource !== 'b2b/employees') {
    return null;
  }

  return canAccess ? (
    <CreateInDialogButton
      resource={resource}
      fullWidth
      maxWidth="lg"
      title={'resources.bo_common.b2b.employees.actions.create.title'}
      testId={`${testId}_OpenButton`}
      ButtonProps={buttonProps}>
      <FormComponent allowCompanyId={allowCompanyId} resource={resource} testId={testId} />
    </CreateInDialogButton>
  ) : null;
};

const FormComponent = ({
  allowCompanyId = false,
  resource,
  testId,
}: B2bEmployeeCreateProps & { testId: string }) => {
  const { data: identity, isLoading: isLoadingIdentity } = useGetIdentity();
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const [create] = useCreatePromise();
  const dataProvider = useDataProvider();
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [formData, setFormData] = useState<FieldValues | null>(null);
  const { close } = useFormDialogContext();

  const onError = useCallback(
    (error: unknown) => {
      notify('resources.bo_common.b2b.employees.actions.create.failure', {
        type: 'warning',
        messageArgs: { error: getErrorMessage(error) },
        multiLine: true,
      });
    },
    [notify],
  );

  const onSuccess = useCallback(() => {
    close();
    notify('resources.bo_common.b2b.employees.actions.create.success', {
      type: 'success',
    });
    redirect(buildLink(resource, 'list'));
    refresh();
  }, [notify, redirect, refresh, resource, close]);

  const handleTransform = useCallback(
    ({ nir_not_yet, nin_not_yet, contract_ids, id_external_taiga, ...data }: RaRecord) => ({
      ...data,
      ...(nir_not_yet
        ? {
            nir: null,
            ntt: data.ntt,
          }
        : {
            nir: data.nir,
            ntt: null,
          }),
      email: data.email ?? null,
      secondary_email: data.secondary_email ?? null,
      phone_number: data.phone_number ?? null,
      work_ending_date: data.work_ending_date,
      tax_number: nin_not_yet ? null : data.tax_number,
      automatic_onboarding: data.automatic_onboarding ?? true,
      taiga_policy_holder_id: id_external_taiga ?? data.taiga_policy_holder_id ?? null,
      contract_ids:
        Array.isArray(contract_ids) && contract_ids.every((id) => typeof id === 'number')
          ? contract_ids
          : [],
    }),
    [],
  );

  const handleCreate = useCallback(
    async (formData: FieldValues | null) => {
      if (formData) {
        try {
          await create(resource, {
            data: handleTransform(formData as RaRecord),
          });
          onSuccess();
        } catch (error: unknown) {
          onError(error);
        }
      }
      setConfirmOpen(false);
    },
    [create, handleTransform, onError, onSuccess, resource],
  );

  let companyId: number | undefined;
  if (resource === 'employees' && isBoExternalIdentity(identity)) {
    companyId = Array.isArray(identity.companyId) ? identity.companyId[0] : identity.companyId;
  }

  const steps: StepperFormStep[] = [
    {
      label: 'resources.bo_common.b2b.employees.actions.create.steps.1',
      content: (
        <PersonalInformationStep
          allowCompanyId={allowCompanyId}
          companyId={companyId}
          testId={`${testId}_PersonalInfoStep`}
        />
      ),
    },
    {
      label: 'resources.bo_common.b2b.employees.actions.create.steps.2',
      content: (
        <EmploymentInformationStep companyId={companyId} testId={`${testId}_EmploymentInfoStep`} />
      ),
    },
    {
      label: 'resources.bo_common.b2b.employees.actions.create.steps.3',
      content: <ContactStep testId={`${testId}_ContactStep`} />,
      validate: createEmployeeEmailsValidator(),
    },
  ];

  if (resource === 'employees' && isLoadingIdentity) {
    return null;
  }

  const onSave = async (formContext: UseFormReturn<FieldValues>) => {
    const values = formContext.getValues();
    const { nir, ntt, date_of_birth, street, postalcode, city, country } = values;
    const company_id = companyId ?? values.company_id;

    if (
      G.isNullable(company_id) ||
      (G.isNullable(nir) && G.isNullable(ntt)) ||
      G.isNullable(date_of_birth)
    ) {
      throw new Error('Invalid form data');
    }

    if (
      G.isNullable(street) ||
      G.isNullable(postalcode) ||
      G.isNullable(city) ||
      G.isNullable(country)
    ) {
      onError(new Error('Home address missing data'));
      return;
    }

    const { data: employees } = await dataProvider.getList<
      GetB2bEmployeesEndpoint['Client']['ResponseBody'][number]
    >(resource, {
      filter: {
        company_id,
        ...(nir && { nir }),
        ...(ntt && { ntt }),
        date_of_birth,
        is_user_closed_or_archived: false,
      },
      sort: { field: 'id', order: 'DESC' },
      pagination: { page: 1, perPage: 1 },
    });

    if (employees.length > 0) {
      setFormData(values);
      setConfirmOpen(true);
    } else {
      await handleCreate(values);
    }
  };

  return (
    <>
      <StepperForm
        defaultValues={{}}
        steps={steps}
        permissions={{
          resource,
          action: 'create',
        }}
        onSave={onSave}
        testId={testId}
      />
      <Confirm
        isOpen={confirmOpen}
        title="Confirmation"
        content="resources.bo_common.b2b.employees.actions.create.confirm_merge_employee"
        onConfirm={async () => handleCreate(formData)}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );
};
